import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAlert } from '../contexts/AlertContext';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowDown, ArrowUp, Bell, Loader2, Minus, Plus, X } from 'lucide-react';
import { dealApi } from '../services/dealApi';
import { companyApi } from '../services/companyApi';
import { AssessStatus, AuditLog, RiskAssessment, RiskQuestion, RiskRound, riskApi } from '../services/riskApi';
import { Deal } from '../types/deal';
import { Company } from '../types/company';
import { GRADE_RANK, RISK_GRADES, RISK_STYLE, RiskGrade } from '../lib/risk';
import { Tour, TourButton, TourStep } from '../components/Tour';
import { nowLocal } from '../lib/date';

/**
 * 리스크 평가 — 분기마다 보유 투자 건의 등급을 매긴다 (factsheet 웨비나 「펀드 결산」 편 대조, 2026-10-09).
 *
 * 평가자(심사역)가 16문항에 답하고 등급 · 의견을 낸다 → 관리자가 승인하면 확정.
 * 제출 뒤에도 승인 전까지는 고칠 수 있다(고치면 작성 중으로 돌아간다). 대시보드 · 재원 상세에는 **승인된 등급만** 쓴다.
 * 「주의 이하」이거나 「전 분기보다 하락」이면 결정권자 알림 목록에 올린다.
 * 상태 전이는 서버(`backend/src/modules/risk`)가 막는다. 평가자 · 승인자 이름은 변경 이력에서 읽는다(로그인 전).
 */
const STATUS_STYLE: Record<AssessStatus, string> = {
  '평가 전': 'bg-slate-100 text-slate-500', '작성 중': 'bg-amber-50 text-amber-700',
  '제출': 'bg-sky-50 text-sky-700', '승인': 'bg-emerald-50 text-emerald-700', '반려': 'bg-red-50 text-red-600',
};
const qLabel = (r: string) => r.replace('-Q', '년 ') + '분기';
const prevCode = (r: string) => { const [y, q] = r.split('-Q').map(Number); return q === 1 ? `${y - 1}-Q4` : `${y}-Q${q - 1}`; };
const nextCode = (r: string) => { const [y, q] = r.split('-Q').map(Number); return q === 4 ? `${y + 1}-Q1` : `${y}-Q${q + 1}`; };
/** 회차가 없으면 지난 분기부터 — 분기가 끝난 뒤 평가한다 */
const firstCode = () => { const d = new Date(); return prevCode(`${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`); };
const day = (iso: string | null) => (iso ? nowLocal(new Date(iso)).slice(0, 10).replace(/-/g, '.') : '');

const GradeChip: React.FC<{ g: RiskGrade | null }> = ({ g }) =>
  g ? <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${RISK_STYLE[g]}`}>{g}</span> : <span className="text-xs text-slate-300">—</span>;

export const RiskReviewPage: React.FC = () => {
  const { showConfirm } = useAlert();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [questions, setQuestions] = useState<RiskQuestion[]>([]);
  const [rounds, setRounds] = useState<RiskRound[]>([]);
  const [rows, setRows] = useState<RiskAssessment[]>([]);
  const [log, setLog] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [roundId, setRoundId] = useState<number | null>(null);
  const [editing, setEditing] = useState<RiskAssessment | null>(null);
  const [rejecting, setRejecting] = useState<{ id: number; reason: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tourRun, setTourRun] = useState(false);

  const reload = useCallback(async () => {
    const [r, a, l] = await Promise.all([riskApi.rounds(), riskApi.assessments(), riskApi.logs()]);
    setRounds(r); setRows(a); setLog(l);
    setRoundId((cur) => (cur && r.some((x) => x.id === cur) ? cur : r[0]?.id ?? null));
  }, []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, c, q] = await Promise.all([dealApi.list(), companyApi.list(), riskApi.questions()]);
      setDeals(d); setCompanies(c); setQuestions(q);
      await reload();
    } catch (e) { setError((e as Error).message); } finally { setLoading(false); }
  }, [reload]);
  useEffect(() => { load(); }, [load]);

  /** 서버 호출 → 다시 읽기. 실패하면 서버 메시지를 그대로 */
  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try { await fn(); await reload(); return true; } catch (e) { setError((e as Error).message); return false; }
  };

  const round = rounds.find((r) => r.id === roundId);
  const code = round?.code ?? '';
  const codeOf = (rid: number) => rounds.find((r) => r.id === rid)?.code ?? '';
  const cName = (dealId: number) => companies.find((c) => c.id === deals.find((d) => d.id === dealId)?.companyId)?.name ?? '—';
  const cId = (dealId: number) => deals.find((d) => d.id === dealId)?.companyId ?? 0;
  const mine = rows.filter((r) => r.roundId === roundId);
  const prevOf = (dealId: number) => rows.find((r) => codeOf(r.roundId) === prevCode(code) && r.dealId === dealId && r.status === '승인')?.grade ?? null;
  const detected = (answers: Record<number, string>) => questions.filter((q) => answers[q.id] && q.riskIf.includes(answers[q.id]));
  // 고른 회차의 기록만 — 다른 회차(지운 회차 포함)의 기록이 섞이지 않게
  const roundIds = new Set(rows.filter((x) => x.roundId === roundId).map((x) => x.id));
  const roundLog = log.filter((l) => (l.entity === 'risk_rounds' && l.entityId === roundId) || (l.entity === 'risk_assessments' && roundIds.has(l.entityId)));
  const who = (id: number, acts: string[]) => log.find((l) => l.entity === 'risk_assessments' && l.entityId === id && acts.includes(l.action))?.actorName;

  const save = async (a: RiskAssessment, submit: boolean) => {
    const ok = await act(async () => { await riskApi.save(a); if (submit) await riskApi.submit(a.id); });
    if (ok) setEditing(null);
  };
  const newRound = async () => {
    const c = rounds.length ? nextCode(rounds[0].code) : firstCode();
    if (!(await showConfirm(`${qLabel(c)} 회차를 만듭니다.\n투자 단계 건이 모두 평가 대상에 들어갑니다.`, { title: '리스크 평가 회차 만들기', confirmText: '회차 만들기' }))) return;
    await act(async () => { const r = await riskApi.createRound(c); setRoundId(r.id); });
  };

  // 결정권자 알림 — 주의 이하 또는 하락
  const alerts = mine.filter((a) => a.grade && (a.status === '제출' || a.status === '승인')).flatMap((a) => {
    const p = prevOf(a.dealId); const out: string[] = [];
    if (GRADE_RANK[a.grade!] >= GRADE_RANK['주의']) out.push(`${cName(a.dealId)} — ${a.grade} (주의 이하)`);
    if (p && GRADE_RANK[a.grade!] > GRADE_RANK[p]) out.push(`${cName(a.dealId)} — ${p} → ${a.grade} 하락`);
    return out;
  });

  const TOUR: TourStep[] = useMemo(() => [
    { name: 'round', target: '[data-tour="round"]', title: '평가 회차', content: '분기마다 한 회차. 「+ 회차」를 누르면 그때 투자 단계인 건이 모두 대상으로 깔립니다.' },
    { name: 'alerts', target: '[data-tour="alerts"]', title: '결정권자 알림', content: '주의 이하이거나 전 분기보다 떨어진 건입니다.' },
    { name: 'table', target: '[data-tour="table"]', title: '평가 현황', content: '평가자가 16문항에 답해 제출하면 관리자가 승인하거나 반려합니다. 승인 전까지 고칠 수 있고(고치면 작성 중으로), 대시보드에는 승인된 등급만 들어갑니다.', placement: 'top' },
    { name: 'trend', target: '[data-tour="trend"]', title: '등급 추이', content: '분기별 승인 등급입니다. 일시적 부진인지 구조적 하락인지 선으로 봅니다.', placement: 'top' },
    { name: 'log', target: '[data-tour="log"]', title: '변경 이력', content: '누가 평가했고 누가 승인했는지, 언제 바뀌었는지 남습니다.', placement: 'top' },
  ], []);

  if (loading) return <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> 불러오는 중...</div>;

  const dealIds = Array.from(new Set(rows.map((r) => r.dealId)));
  const trend = [...rounds].reverse();

  return (
    <div className="p-6 lg:p-8 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-1.5">리스크 평가 <TourButton onClick={() => setTourRun(true)} /></h1>
        <div data-tour="round" className="flex items-center gap-2">
          {rounds.length > 0 && (
            <select aria-label="평가 회차" value={roundId ?? ''} onChange={(e) => setRoundId(Number(e.target.value))} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white">
              {rounds.map((r) => <option key={r.id} value={r.id}>{qLabel(r.code)}</option>)}
            </select>
          )}
          <button onClick={newRound} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 bg-white hover:bg-slate-50"><Plus size={13} /> 회차</button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-xs text-red-700 flex justify-between">
          <span>{error}</span><button onClick={() => setError(null)} className="text-red-500 hover:text-red-800">닫기</button>
        </div>
      )}

      {!round ? (
        <div className="bg-white border border-slate-200 rounded-lg py-12 mb-4 text-center">
          <p className="text-sm text-slate-600">아직 리스크 평가 회차가 없습니다.</p>
          <p className="mt-1 text-xs text-slate-400">오른쪽 위 「회차」로 분기 회차를 만들면 투자 단계 건이 모두 평가 대상에 들어갑니다.</p>
        </div>
      ) : (<>
        <div className="grid gap-2 grid-cols-3 sm:grid-cols-6 mb-4">
          {(['평가 전', '작성 중', '제출', '반려', '승인'] as AssessStatus[]).map((s) => (
            <div key={s} className="bg-white border border-slate-200 rounded-lg px-3 py-2"><div className="text-xs text-slate-400">{s}</div><div className="text-lg font-bold tabular-nums">{mine.filter((a) => a.status === s).length}</div></div>
          ))}
          <div className="bg-white border border-slate-200 rounded-lg px-3 py-2"><div className="text-xs text-slate-400">대상</div><div className="text-lg font-bold tabular-nums">{mine.length}</div></div>
        </div>

        <section data-tour="alerts" className={`mb-4 rounded-lg border px-4 py-2.5 ${alerts.length ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-white'}`}>
          <h2 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900"><Bell size={14} className={alerts.length ? 'text-amber-700' : 'text-slate-400'} /> 결정권자 알림 {alerts.length}</h2>
          {alerts.length > 0 && <ul className="mt-1 text-xs text-amber-900 space-y-0.5">{alerts.map((a) => <li key={a}>{a}</li>)}</ul>}
        </section>

        <div data-tour="table" className="bg-white rounded-lg border border-slate-200 overflow-x-auto mb-5">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-xs text-slate-400">
              <th className="text-left font-medium px-4 py-2.5">투자처</th><th className="text-left font-medium px-2">평가자 · 관리자</th>
              <th className="text-left font-medium px-2">전 분기</th><th className="text-left font-medium px-2">이번 분기</th><th className="text-left font-medium px-2">감지</th>
              <th className="text-left font-medium px-2">상태</th><th className="text-right font-medium px-4">처리</th>
            </tr></thead>
            <tbody>
              {mine.map((a) => {
                const p = prevOf(a.dealId);
                const move = a.grade && p ? GRADE_RANK[a.grade] - GRADE_RANK[p] : 0;
                return (
                  <tr key={a.id} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-4 py-3"><Link to={`/companies/${cId(a.dealId)}`} className="font-medium text-slate-900 hover:text-indigo-600">{cName(a.dealId)}</Link></td>
                    <td className="px-2 text-xs text-slate-600">{who(a.id, ['save', 'submit']) ?? '—'} · {who(a.id, ['approve', 'reject']) ?? '—'}</td>
                    <td className="px-2"><GradeChip g={p} /></td>
                    <td className="px-2"><span className="inline-flex items-center gap-1"><GradeChip g={a.grade} />
                      {a.grade && p && (move > 0 ? <ArrowDown size={13} className="text-red-500" aria-label="하락" /> : move < 0 ? <ArrowUp size={13} className="text-emerald-600" aria-label="상승" /> : <Minus size={13} className="text-slate-300" aria-label="유지" />)}</span></td>
                    <td className="px-2 text-xs">{detected(a.answers).length > 0 ? <span className="text-amber-700">{detected(a.answers).length}건</span> : <span className="text-slate-300">—</span>}</td>
                    <td className="px-2"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[a.status]}`} title={a.rejectReason ?? undefined}>{a.status}</span></td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {rejecting?.id === a.id ? (
                        <form className="inline-flex gap-1" onSubmit={async (e) => { e.preventDefault(); if (await act(() => riskApi.reject(a.id, rejecting.reason))) setRejecting(null); }}>
                          <input autoFocus aria-label="반려 사유" placeholder="반려 사유" value={rejecting.reason} onChange={(e) => setRejecting({ id: a.id, reason: e.target.value })} className="px-2 py-1 text-xs border border-slate-200 rounded-lg w-40" />
                          <button type="submit" disabled={!rejecting.reason.trim()} className="px-2.5 py-1 rounded-lg text-xs font-medium bg-red-600 text-white disabled:opacity-40">반려</button>
                          <button type="button" aria-label="취소" onClick={() => setRejecting(null)} className="px-1.5 text-slate-400 hover:text-slate-700"><X size={13} /></button>
                        </form>
                      ) : (<>
                        {a.status !== '승인' && (
                          <button onClick={() => setEditing({ ...a, answers: { ...a.answers } })} className="px-2.5 py-1 rounded-lg text-xs font-medium border border-slate-200 hover:bg-slate-50">{a.status === '평가 전' ? '평가하기' : '고치기'}</button>
                        )}
                        {a.status === '제출' && (
                          <>
                            <button onClick={() => act(() => riskApi.approve(a.id))} className="ml-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700">승인</button>
                            <button onClick={() => setRejecting({ id: a.id, reason: '' })} className="ml-1 px-2.5 py-1 rounded-lg text-xs font-medium text-red-600 hover:bg-red-50">반려</button>
                          </>
                        )}
                        {a.status === '승인' && <span className="text-xs text-slate-400 tabular-nums">{day(a.approvedAt)} 확정</span>}
                      </>)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </>)}

      <div className="grid gap-4 lg:grid-cols-2">
        <section data-tour="trend" className="bg-white border border-slate-200 rounded-lg">
          <h2 className="text-sm font-semibold text-slate-900 px-4 py-3 border-b border-slate-100">등급 추이 · 승인 기준</h2>
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-slate-400"><th className="text-left font-medium px-4 py-2">투자처</th>{trend.map((r) => <th key={r.id} className="text-center font-medium px-2">{r.code.slice(2).replace('-Q', ' Q')}</th>)}</tr></thead>
            <tbody>
              {(dealIds.length === 0 || trend.length === 0) && <tr><td colSpan={trend.length + 1} className="px-4 py-6 text-center text-xs text-slate-400">승인된 등급이 생기면 분기별로 쌓입니다</td></tr>}
              {dealIds.map((id) => (
              <tr key={id} className="border-t border-slate-50">
                <td className="px-4 py-2">{cName(id)}</td>
                {trend.map((r) => <td key={r.id} className="px-2 text-center"><GradeChip g={rows.find((x) => x.roundId === r.id && x.dealId === id && x.status === '승인')?.grade ?? null} /></td>)}
              </tr>))}</tbody>
          </table>
        </section>
        <section data-tour="log" className="bg-white border border-slate-200 rounded-lg">
          <h2 className="text-sm font-semibold text-slate-900 px-4 py-3 border-b border-slate-100">변경 이력 · 이 회차</h2>
          <ul className="divide-y divide-slate-50 max-h-64 overflow-y-auto">
            {roundLog.length === 0 && <li className="px-4 py-3 text-sm text-slate-400">기록 없음</li>}
            {roundLog.map((l) => <li key={l.id} className="px-4 py-2 text-sm"><span className="text-xs text-slate-400 tabular-nums mr-2">{nowLocal(new Date(l.createdAt))}</span><span className="text-xs font-medium text-slate-600 mr-1.5">{l.actorName}</span>{l.summary}</li>)}
          </ul>
        </section>
      </div>

      {editing && <AssessModal a={editing} code={code} questions={questions} name={cName(editing.dealId)} prev={prevOf(editing.dealId)} onClose={() => setEditing(null)} onSave={save} />}
      <Tour steps={TOUR} run={tourRun} onFinish={() => setTourRun(false)} />
    </div>
  );
};

const AssessModal: React.FC<{
  a: RiskAssessment; code: string; questions: RiskQuestion[]; name: string; prev: RiskGrade | null;
  onClose: () => void; onSave: (a: RiskAssessment, submit: boolean) => void;
}> = ({ a, code, questions, name, prev, onClose, onSave }) => {
  const [form, setForm] = useState<RiskAssessment>(a);
  const set = (id: number, v: string) => setForm((f) => ({ ...f, answers: { ...f.answers, [id]: v } }));
  const found = questions.filter((q) => form.answers[q.id] && q.riskIf.includes(form.answers[q.id]));
  const answered = questions.filter((q) => form.answers[q.id]).length;
  const areas = Array.from(new Set(questions.map((q) => q.area)));
  // 제출에 빠진 것 — 단추가 왜 잠겼는지 단추 옆에 적는다 (서버도 같은 규칙)
  const missing = [
    answered < questions.length && `문항 ${questions.length - answered}`,
    form.grade === null && '등급',
    !form.opinion.trim() && '평가 의견',
  ].filter(Boolean) as string[];
  const canSubmit = missing.length === 0;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-3xl my-8">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-bold text-slate-900">{name} · {qLabel(code)} 리스크 평가</h2>
            <p className="text-xs text-slate-500">문항 {answered}/{questions.length} · 전 분기 {prev ?? '없음'}{a.status === '반려' && a.rejectReason ? ` · 반려 — ${a.rejectReason}` : ''}</p>
          </div>
          <button onClick={onClose} aria-label="닫기" className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {areas.map((ar) => (
            <fieldset key={ar} className="space-y-2">
              <legend className="text-xs font-semibold text-slate-500 mb-1">{ar}</legend>
              {questions.filter((q) => q.area === ar).map((q) => {
                const flagged = form.answers[q.id] && q.riskIf.includes(form.answers[q.id]);
                return (
                  <div key={q.id} className={`flex flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-2 ${flagged ? 'bg-amber-50' : 'bg-slate-50'}`}>
                    <span className="text-sm text-slate-800">{q.text}</span>
                    <div className="flex flex-wrap gap-1" role="radiogroup" aria-label={q.text}>
                      {q.options.map((o) => (
                        <button key={o} type="button" role="radio" aria-checked={form.answers[q.id] === o} onClick={() => set(q.id, o)}
                          className={`px-2 py-0.5 rounded-md text-xs border ${form.answers[q.id] === o ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'}`}>{o}</button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </fieldset>
          ))}
        </div>
        <div className="px-5 py-4 border-t border-slate-100 space-y-3">
          <div className="text-xs">
            <span className="font-semibold text-slate-600">감지된 리스크 {found.length}</span>
            {found.length > 0 && <span className="ml-2 text-amber-800">{found.map((q) => q.text.replace(/[?]/g, '')).join(' · ')}</span>}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-slate-500 mr-1">최종 등급</span>
            {RISK_GRADES.map((g) => (
              <button key={g} type="button" onClick={() => setForm((f) => ({ ...f, grade: g }))}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${form.grade === g ? `${RISK_STYLE[g]} border-current` : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>{g}</button>
            ))}
          </div>
          <label className="block">
            <span className="text-xs text-slate-500">평가 의견</span>
            <textarea id="risk-opinion" value={form.opinion} onChange={(e) => setForm((f) => ({ ...f, opinion: e.target.value }))}
              className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded-lg h-16 resize-none" />
          </label>
          {prev && form.grade && GRADE_RANK[form.grade] > GRADE_RANK[prev] && (
            <p className="flex items-center gap-1 text-xs text-amber-800"><AlertTriangle size={12} /> 전 분기({prev})보다 낮음 — 제출하면 결정권자 알림</p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
          {missing.length > 0 && <span className="mr-auto text-xs text-slate-500">제출 전 — {missing.join(' · ')}</span>}
          <button onClick={() => onSave(form, false)} className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50">임시 저장</button>
          <button onClick={() => onSave(form, true)} disabled={!canSubmit} className="px-5 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">제출</button>
        </div>
      </div>
    </div>
  );
};
